import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LoadingButton } from '@mui/lab';
import { PieChart } from '@mui/x-charts/PieChart';
import moment from 'moment';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import PermissionRequired from '../../../errorPages/permissionRequired';
import useSettings from '../../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { useSelector } from '../../../../redux/store';
import { get } from '../../../../inteceptor';
import { accessVerify, utcMoment } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';

const CLOSED_ORDER_STATUSES = ['Refunded', 'Canceled'];

const SESSION_STATUS_LABELS = {
  active: 'Active',
  transferred: 'Transferred',
  drawer_closed: 'Drawer Closed',
};

const formatDateTime = (value) => (value ? utcMoment(value).format('YYYY-MM-DD HH:mm:ss') : '-');
const formatStatus = (status) => SESSION_STATUS_LABELS[status] || status || '-';

const getStatusColor = (status) => {
  if (status === 'active') return 'warning';
  if (status === 'transferred') return 'info';
  if (status === 'drawer_closed') return 'success';
  return 'default';
};

const buildPaymentSummary = (payments = [], orders = []) => {
  const orderStatusById = new Map(orders.map((order) => [Number(order.id), order.status]));
  const grouped = new Map();

  payments.forEach((payment) => {
    if (CLOSED_ORDER_STATUSES.includes(orderStatusById.get(Number(payment?.posId)))) return;
    if (payment?.paymentTypesId === null || payment?.paymentTypesId === undefined) return;
    if (!grouped.has(payment.paymentTypesId)) {
      grouped.set(payment.paymentTypesId, {
        id: payment.paymentTypesId,
        name: payment.paymentTypeName || 'Unknown',
        totalAmount: 0,
        transactionCount: 0,
        details: [],
      });
    }
    const group = grouped.get(payment.paymentTypesId);
    group.totalAmount += Number(payment.amount || 0);
    group.transactionCount += 1;
    group.details.push(payment);
  });

  return [...grouped.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const getTransferText = (transfer) => {
  if (!transfer) return 'No transfer recorded';
  if (transfer.incoming) return `Received from ${transfer.incoming.sourceUserName || 'previous cashier'} (${transfer.incoming.status})`;
  if (transfer.outgoing?.length) {
    const latest = transfer.outgoing[transfer.outgoing.length - 1];
    return `Transferred to ${latest.toUserName || 'next cashier'} (${latest.status})`;
  }
  return 'No transfer recorded';
};

function SessionPaymentBreakdown({ paymentSummary }) {
  const chartData = paymentSummary.map((paymentType) => ({ id: paymentType.id, value: paymentType.totalAmount, label: paymentType.name }));

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Card sx={{ p: 3, minHeight: 360 }}>
          <Typography variant='h6'>Payment Distribution</Typography>
          {chartData.length > 0 ? (
            <PieChart
              series={[
                {
                  data: chartData,
                  innerRadius: 55,
                  outerRadius: 110,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 45, additionalRadius: -12 },
                },
              ]}
              height={300}
            />
          ) : (
            <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                No payment data is available for this session.
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={7}>
        <Card sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Payment Details
          </Typography>
          {paymentSummary.length > 0 ? (
            paymentSummary.map((paymentType) => (
              <Box key={paymentType.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.100', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant='subtitle1'>{paymentType.name}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {paymentType.transactionCount} entr{paymentType.transactionCount === 1 ? 'y' : 'ies'}
                    </Typography>
                  </Box>
                  <Typography variant='subtitle1' sx={{ whiteSpace: 'nowrap', color: 'primary.main' }}>
                    {fCurrency(paymentType.totalAmount)}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Verifier ID</TableCell>
                        <TableCell align='right'>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentType.details.map((detail, index) => (
                        <TableRow key={`${paymentType.id}-${detail?.id || index}`} hover>
                          <TableCell>{detail?.posId || '-'}</TableCell>
                          <TableCell>{detail?.orderStatus || '-'}</TableCell>
                          <TableCell>{detail?.verifierId || '-'}</TableCell>
                          <TableCell align='right'>{fCurrency(detail?.amount || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          ) : (
            <Typography variant='body2' color='text.secondary'>
              No payment summary is available.
            </Typography>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

function TransferSummary({ transfer }) {
  const outgoing = transfer?.outgoing || [];
  return (
    <Card sx={{ p: 3 }}>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Transfer Information
      </Typography>
      <Typography variant='body1'>{getTransferText(transfer)}</Typography>
      {transfer?.incoming && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Transfer #{transfer.incoming.id} accepted at {formatDateTime(transfer.incoming.acceptedAt)}
        </Typography>
      )}
      {outgoing.length > 0 && (
        <TableContainer sx={{ mt: 2 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Transfer ID</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Accepted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outgoing.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.toUserName || '-'}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  <TableCell>{formatDateTime(item.acceptedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

function SessionSummary({ sessionData }) {
  const session = sessionData?.session;
  const orders = sessionData?.orders || [];
  const payments = sessionData?.payments || [];
  const totals = sessionData?.totals || {};
  const paymentSummary = useMemo(() => buildPaymentSummary(payments, orders), [orders, payments]);

  if (!session)
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant='subtitle1'>Select a cashier session to view its reconciliation report.</Typography>
      </Card>
    );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={2}>
            <Box>
              <Typography variant='h5'>Session #{session.id}</Typography>
              <Typography variant='body2' color='text.secondary'>
                Drawer #{session.drawerId} - Sequence {session.sequenceNo} - {session.drawer?.locationName || '-'} - Started {formatDateTime(session.startedAt)} - Ended{' '}
                {formatDateTime(session.endedAt)}
              </Typography>
            </Box>
            <Chip label={formatStatus(session.status)} color={getStatusColor(session.status)} sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }} />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant='overline' color='text.secondary'>
                Cashier / Session Operator
              </Typography>
              <Typography variant='subtitle2'>{session.cashier?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='overline' color='text.secondary'>
                Started By
              </Typography>
              <Typography variant='subtitle2'>{session.startedBy?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='overline' color='text.secondary'>
                Ended By
              </Typography>
              <Typography variant='subtitle2'>{session.endedBy?.name || '-'}</Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <TransferSummary transfer={sessionData.transfer} />
      </Grid>
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant='overline' color='text.secondary'>
            Session Payment Total
          </Typography>
          <Typography variant='h3' sx={{ mt: 1 }}>
            {fCurrency(totals.paymentAmount || 0)}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                Sales
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(totals.salesAmount || 0)}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                Payment Total
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(totals.paymentAmount || 0)}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                Reconciled Orders
              </Typography>
              <Typography variant='subtitle2'>{totals.includedOrderCount || 0}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                All Orders
              </Typography>
              <Typography variant='subtitle2'>{totals.orderCount || 0}</Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <SessionPaymentBreakdown paymentSummary={paymentSummary} />
      </Grid>
      <Grid item xs={12}>
        <Card sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Orders In Session
          </Typography>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Order No</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Order Total</TableCell>
                  <TableCell align='right'>Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{formatDateTime(order.dateTime)}</TableCell>
                    <TableCell>{order.status || '-'}</TableCell>
                    <TableCell align='right'>{fCurrency(order.totalAmount || 0)}</TableCell>
                    <TableCell align='right'>{fCurrency((order.posPayments || []).reduce((sum, payment) => sum + Number(payment?.amount || 0), 0))}</TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant='body2' color='text.secondary'>
                        No orders found for this session.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
    </Grid>
  );
}

export default function POSCashierSessionReconciliationReport() {
  const { themeStretch } = useSettings();
  const { selectedLocation } = useSelector((state) => state.location);
  const [sessionList, setSessionList] = useState([]);
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [date, setDate] = useState(moment().startOf('day'));
  const [isLoading, setIsLoading] = useState(false);

  const fetchSessions = async () => {
    if (!selectedLocation?.id || !date) return;
    setSelectedSessionData(null);
    setIsLoading(true);
    try {
      const response = await get('posCashierSessionReconciliation', {
        locationId: selectedLocation.id,
        date: moment(date).format('YYYY-MM-DD'),
        status: selectedStatus,
      });
      if (response?.status) setSessionList(response.data || []);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSessionDetails = async (session) => {
    if (!session?.id) return;
    const response = await get(`posCashierSessionReconciliation/${session.id}`);
    if (response?.status) setSelectedSessionData(response.data);
  };

  useEffect(() => {
    fetchSessions();
  }, [selectedLocation, selectedStatus, date]);

  const columns = [
    { accessorKey: 'id', header: 'Session ID' },
    { accessorKey: 'drawerId', header: 'Drawer ID' },
    { accessorKey: 'locationName', header: 'Location', Cell: ({ row }) => row.original.locationName || '-' },
    { accessorKey: 'sequenceNo', header: 'Sequence' },
    { accessorKey: 'cashier', header: 'Cashier', Cell: ({ row }) => row.original.cashier?.name || '-' },
    { accessorKey: 'startedAt', header: 'Started', Cell: ({ row }) => formatDateTime(row.original.startedAt) },
    { accessorKey: 'endedAt', header: 'Ended', Cell: ({ row }) => formatDateTime(row.original.endedAt) },
    { accessorKey: 'orderCount', header: 'Orders' },
    { accessorKey: 'salesAmount', header: 'Sales', Cell: ({ row }) => fCurrency(row.original.salesAmount || 0) },
    { accessorKey: 'paymentAmount', header: 'Payments', Cell: ({ row }) => fCurrency(row.original.paymentAmount || 0) },
    { accessorKey: 'transfer', header: 'Transfer', Cell: ({ row }) => getTransferText(row.original.transfer) },
    { accessorKey: 'status', header: 'Status', Cell: ({ row }) => <Chip label={formatStatus(row.original.status)} color={getStatusColor(row.original.status)} size='small' /> },
  ];

  return (
    <Page title='POS Session Reconciliation'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs heading='POS Session Reconciliation' links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Reports', href: '' }, { name: 'POS Session Reconciliation' }]} />
        {accessVerify('POS_SESSION_RECONCILIATION_REPORT_VIEW') ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={3}>
                    <DatePicker label='Date' value={date} onChange={(newValue) => newValue && setDate(moment(newValue))} renderInput={(params) => <TextField {...params} fullWidth />} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id='session-status-label'>Status</InputLabel>
                      <Select labelId='session-status-label' value={selectedStatus} label='Status' onChange={(event) => setSelectedStatus(event.target.value)}>
                        <MenuItem value='all'>All</MenuItem>
                        <MenuItem value='active'>Active</MenuItem>
                        <MenuItem value='transferred'>Transferred</MenuItem>
                        <MenuItem value='drawer_closed'>Drawer Closed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LoadingButton fullWidth variant='contained' size='large' loading={isLoading} onClick={fetchSessions}>
                      Load Sessions
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <DataGridTable
                name='POS Session Reconciliation Sessions'
                data={sessionList}
                column={columns}
                onRowClick={(row) => fetchSessionDetails(row?.original)}
                isRowClickable
                rowSelection={false}
                setRowSelection={() => {}}
                enableRowSelection={false}
                enableRowActions={false}
                renderRowActionItems={() => []}
              />
            </Grid>
            <Grid item xs={12}>
              <SessionSummary sessionData={selectedSessionData} />
            </Grid>
          </Grid>
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
}
